'use client'

/**
 * Reset Storage Button Component
 *
 * Allows users to wipe local storage and IndexedDB to resync from profile.
 * Useful for troubleshooting sync issues or starting fresh on a new device.
 *
 * Features:
 * - Clears localStorage (document URL)
 * - Clears IndexedDB (Automerge document data)
 * - Option to also clear document URL from profile
 * - Confirmation dialog to prevent accidental resets
 * - Automatic page reload after reset
 */

import { useState } from 'react'
import { Button } from '@/ui/button'
import { Alert, AlertDescription } from '@/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/alert-dialog'
import { TrashIcon, WarningIcon, SpinnerGapIcon } from '@phosphor-icons/react'
import { Checkbox } from '@/ui/checkbox'
import { Label } from '@/ui/label'
import { getEnvironmentStorageKey, getCurrentOrigin } from '@/lib/environment'

type ResetState = 'idle' | 'resetting' | 'success' | 'failed'

// Lazy evaluation - called only in browser context
const getStorageKey = () => getEnvironmentStorageKey('automerge-doc-url')

export function ResetStorageButton() {
  const [state, setState] = useState<ResetState>('idle')
  const [error, setError] = useState<string>('')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [clearProfile, setClearProfile] = useState(false) // Default: keep profile URL

  const handleReset = async () => {
    setShowConfirmDialog(false)
    setState('resetting')
    setError('')

    try {
      // Step 1: Clear localStorage
      localStorage.removeItem(getStorageKey())

      // Step 2: Clear IndexedDB
      // Delete known IndexedDB databases used by automerge-repo
      const dbNames = ['automerge'] // Default automerge-repo database name

      // Try to get all databases if the API is available (not all browsers support it)
      if ('databases' in window.indexedDB) {
        try {
          const databases = await window.indexedDB.databases()
          databases.forEach((db) => {
            if (db.name && !dbNames.includes(db.name)) {
              dbNames.push(db.name)
            }
          })
        } catch (e) {
          console.warn('Could not enumerate databases:', e)
          // Continue with known database names
        }
      }

      // Delete all identified databases
      for (const dbName of dbNames) {
        await new Promise<void>((resolve, reject) => {
          const request = window.indexedDB.deleteDatabase(dbName)
          request.onsuccess = () => resolve()
          request.onerror = () => {
            console.warn(`Failed to delete database ${dbName}:`, request.error)
            resolve() // Don't fail the entire reset if one DB deletion fails
          }
          request.onblocked = () => {
            console.warn(`Database ${dbName} deletion blocked - close all tabs`)
            resolve() // Continue anyway
          }
        })
      }

      // Step 3: Clear document URL from profile for this environment if requested
      if (clearProfile) {
        const origin = getCurrentOrigin()
        const { saveAutomergeDocumentUrl } =
          await import('../discovery/actions')
        await saveAutomergeDocumentUrl('', origin)
      }

      setState('success')

      // Reload page after 1.5 seconds to reinitialize
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (err) {
      setState('failed')
      setError(
        err instanceof Error ? err.message : 'Failed to reset local storage',
      )
    }
  }

  const handleRetry = () => {
    setState('idle')
    setError('')
  }

  return (
    <div className="space-y-4">
      {/* Idle state: Show reset button */}
      {state === 'idle' && (
        <>
          <Button
            onClick={() => setShowConfirmDialog(true)}
            variant="destructive"
            size="lg"
            className="w-full"
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            Reset Local Storage
          </Button>

          <AlertDialog
            open={showConfirmDialog}
            onOpenChange={setShowConfirmDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <WarningIcon className="text-warning h-5 w-5" />
                  Reset Local Storage?
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <p>
                    This will delete all local data for{' '}
                    <strong>this environment</strong> and resync from your
                    profile.
                  </p>

                  <div className="border-warning/30 bg-warning/5 rounded-lg border p-3">
                    <p className="text-foreground text-sm font-medium">
                      What will be deleted (this environment only):
                    </p>
                    <ul className="text-muted-foreground mt-2 list-inside list-disc space-y-1 text-sm">
                      <li>Local document storage (IndexedDB)</li>
                      <li>Document URL from localStorage</li>
                      <li>
                        All offline changes that haven&apos;t synced will be
                        lost
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="clearProfile"
                      checked={clearProfile}
                      onCheckedChange={(checked) =>
                        setClearProfile(checked === true)
                      }
                    />
                    <Label
                      htmlFor="clearProfile"
                      className="text-sm font-normal"
                    >
                      Also clear document URL from profile for this environment
                      (only if starting completely fresh)
                    </Label>
                  </div>

                  <p className="font-medium">
                    Are you sure you want to continue?
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReset}
                  className="bg-destructive hover:bg-destructive/90 text-white"
                >
                  Reset Local Storage
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {/* Resetting state: Show progress */}
      {state === 'resetting' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <SpinnerGapIcon className="h-5 w-5 animate-spin" />
            <span className="font-medium">Resetting local storage...</span>
          </div>
          <p className="text-muted-foreground text-sm">
            Clearing IndexedDB and localStorage...
          </p>
        </div>
      )}

      {/* Success state: Show success message */}
      {state === 'success' && (
        <Alert className="border-success/30 bg-success/5">
          <AlertDescription className="text-foreground">
            <div className="space-y-2">
              <p className="font-medium">Local storage reset successfully!</p>
              <p className="text-sm">Reloading app to resync...</p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Failed state: Show error and retry option */}
      {state === 'failed' && (
        <div className="space-y-4">
          <Alert variant="destructive">
            <WarningIcon className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">Reset failed</p>
                <p className="text-sm">{error}</p>
              </div>
            </AlertDescription>
          </Alert>

          <Button
            onClick={handleRetry}
            variant="outline"
            size="lg"
            className="w-full"
          >
            Try Again
          </Button>
        </div>
      )}
    </div>
  )
}
